import { EllipsisOutlined } from '@ant-design/icons'
import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { format, formatDistanceToNow } from 'date-fns'
import React from 'react'
import { useNavigate } from 'react-router'
import { DropdownMenu } from 'sutra/components'
import { Checkbox } from 'sutra/components/Checkbox/Checkbox'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Toggle } from 'sutra/components/Toggle/Toggle'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { LiveStatusDisplay } from 'features/projects/components/LiveStatusDisplay'
import {
  ActionButton,
  ArchiveActionLabel,
  ArchivedHelpIcon,
  HelpIcon,
  ProjectsWrapper,
  StyledTable,
  TitleText,
  TooltipContent,
  TooltipSection,
  TooltipSteps,
  TooltipText,
  TypeTagsWrapper,
} from 'features/projects/components/project-list/project-list-styles'
import {
  DATA_TYPE_LABELS,
  ProjectDataType,
  ProjectUsage,
  USAGE_LABELS,
} from 'features/projects/constants'
import { projectPath } from 'routes/builders'
import { GroupProject, Project } from 'types/project'
import { numberFormatter } from 'utils/number-format'

// Derived from the Table: its row-selection checkboxes stay antd.
type RowCheckboxProps = ReturnType<
  NonNullable<
    NonNullable<TableProps<Project>['rowSelection']>['getCheckboxProps']
  >
>

export type Props = {
  handleRemoveProject: (a: string) => void
  onEditProject: (project: Project) => void
  onArchiveProject: (uuid: string) => void
  onInlineToggleInternal: (uuid: string, value: boolean) => void
  onSelectedChange: (keys: string[]) => void
  selectedRowKeys: string[]
  loading: boolean
  projects: Project[] | null
  isAdmin: boolean
  highlightedIndex: number | null
  currentPage: number
  pageSize: number
  onPageChange: (page: number, pageSize: number) => void
  onTableChange: (currentDataSource: Project[]) => void
  showRelativeTime: boolean
}

export const ProjectList = ({
  projects,
  loading,
  handleRemoveProject,
  onEditProject,
  onArchiveProject,
  onInlineToggleInternal,
  onSelectedChange,
  selectedRowKeys,
  isAdmin,
  highlightedIndex,
  currentPage,
  pageSize,
  onPageChange,
  onTableChange,
  showRelativeTime,
}: Props): React.JSX.Element => {
  const navigate = useNavigate()

  const formatRelative = (timestamp: number): string =>
    `${formatDistanceToNow(new Date(timestamp * 1000))} ago`.replace(
      'about ',
      '',
    )

  const columns: ColumnsType<Project> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 400,
      fixed: 'left',
      sorter: (a, b) => a.title.localeCompare(b.title),
      showSorterTooltip: false,
      defaultSortOrder: 'ascend',
      render: (text: string) => <TitleText>{text}</TitleText>,
    },
    {
      title: 'Internal',
      dataIndex: 'is_internal',
      key: 'is_internal',
      width: 90,
      align: 'center',
      sorter: (a, b) => Number(a.is_internal) - Number(b.is_internal),
      showSorterTooltip: false,
      render: (value: boolean, record: Project) =>
        isAdmin ? (
          <span onClick={(e) => e.stopPropagation()}>
            <Toggle
              size="sm"
              checked={value}
              onChange={(checked) =>
                onInlineToggleInternal(record.uuid, checked)
              }
            />
          </span>
        ) : value ? (
          <Checkbox checked disabled />
        ) : null,
    },
    {
      title: 'Usage',
      dataIndex: 'usage',
      key: 'usage',
      width: 110,
      sorter: (a, b) => (a.usage ?? '').localeCompare(b.usage ?? ''),
      showSorterTooltip: false,
      render: (value: string | null) =>
        value ? (
          <Chiclet $tone="gray">
            {USAGE_LABELS[value as ProjectUsage] ?? value}
          </Chiclet>
        ) : (
          '-'
        ),
    },
    {
      title: 'Data Types',
      dataIndex: 'data_types',
      key: 'data_types',
      width: 200,
      sorter: (a, b) =>
        (a.data_types?.join(',') ?? '').localeCompare(
          b.data_types?.join(',') ?? '',
        ),
      showSorterTooltip: false,
      render: (value: string[] | null) =>
        value?.length ? (
          <TypeTagsWrapper>
            {value.map((dt) => (
              <Chiclet key={dt} $tone="gray">
                {DATA_TYPE_LABELS[dt as ProjectDataType] ?? dt}
              </Chiclet>
            ))}
          </TypeTagsWrapper>
        ) : null,
    },
    {
      title: 'Brands',
      dataIndex: 'n_brands',
      key: 'n_brands',
      width: 100,
      sorter: (a, b) => a.n_brands - b.n_brands,
      showSorterTooltip: false,
      render: (value: number) => numberFormatter(value),
      align: 'right',
    },
    {
      title: (
        <span>
          Products{' '}
          <Tooltip
            content={
              <TooltipText>Only includes products with reviews</TooltipText>
            }
          >
            <HelpIcon />
          </Tooltip>
        </span>
      ),
      dataIndex: 'n_products',
      key: 'n_products',
      width: 120,
      sorter: (a, b) => a.n_products - b.n_products,
      showSorterTooltip: false,
      render: (value: number) => numberFormatter(value),
      align: 'right',
    },
    {
      title: 'Sources',
      dataIndex: 'n_sources',
      key: 'n_sources',
      width: 100,
      sorter: (a, b) => a.n_sources - b.n_sources,
      showSorterTooltip: false,
      render: (value: number) => numberFormatter(value),
      align: 'right',
    },
    {
      title: 'Child Datasets',
      dataIndex: 'groups',
      key: 'n_datasets',
      width: 140,
      sorter: (a, b) => (a.groups?.length || 0) - (b.groups?.length || 0),
      showSorterTooltip: false,
      render: (groups: GroupProject[] | null) =>
        numberFormatter(groups?.length || 0),
      align: 'right',
    },
    {
      title: 'Reviews',
      dataIndex: 'n_posts',
      key: 'n_posts',
      width: 100,
      sorter: (a, b) => a.n_posts - b.n_posts,
      showSorterTooltip: false,
      render: (value: number) => numberFormatter(value),
      align: 'right',
    },
    {
      title: 'Ratings-only',
      dataIndex: 'n_ratings',
      key: 'n_ratings',
      width: 120,
      sorter: (a, b) => a.n_ratings - b.n_ratings,
      showSorterTooltip: false,
      render: (value: number) => numberFormatter(value),
      align: 'right',
    },
    {
      title: (
        <span>
          Processing Status{' '}
          <Tooltip
            content={
              <TooltipContent>
                <TooltipSection>
                  <strong>Reading Data (Steps 1-5)</strong>
                  <TooltipSteps>
                    1. Created
                    <br />
                    2. Preprocessing
                    <br />
                    3. Preprocessing Update
                    <br />
                    4. Preprocessing Complete
                    <br />
                    5. Preprocessing Update Complete
                  </TooltipSteps>
                </TooltipSection>
                <TooltipSection>
                  <strong>Network Processing (Steps 6-9)</strong>
                  <TooltipSteps>
                    6. Network Processing
                    <br />
                    7. Network Processing Update
                    <br />
                    8. Network Complete
                    <br />
                    9. Network Update Complete
                  </TooltipSteps>
                </TooltipSection>
                <TooltipSection>
                  <strong>Clustering &amp; Embedding (Steps 10-11)</strong>
                  <TooltipSteps>
                    10. Clustering Update Complete
                    <br />
                    11. Create Embeddings Update Complete
                  </TooltipSteps>
                </TooltipSection>
                <div>
                  <strong>Complete</strong>
                  <TooltipSteps>
                    {'• Success'}
                    <br />
                    {'• Error'}
                  </TooltipSteps>
                </div>
              </TooltipContent>
            }
          >
            <HelpIcon />
          </Tooltip>
        </span>
      ),
      dataIndex: 'state',
      key: 'state',
      width: 210,
      sorter: (a, b) => a.state.localeCompare(b.state),
      showSorterTooltip: false,
      render: (value: string) => <LiveStatusDisplay state={value} />,
    },
    {
      title: 'Last Scrape Date',
      dataIndex: 'last_update',
      key: 'last_update',
      width: 160,
      sorter: (a, b) => (a.last_update || 0) - (b.last_update || 0),
      showSorterTooltip: false,
      render: (timestamp: number | undefined) =>
        timestamp
          ? showRelativeTime
            ? formatRelative(timestamp)
            : format(new Date(timestamp * 1000), 'MMM dd, yyyy')
          : '-',
      align: 'right',
    },
    {
      title: 'Last Clustered Record Date',
      dataIndex: 'last_record_date',
      key: 'last_record_date',
      width: 160,
      sorter: (a, b) => (a.last_record_date || 0) - (b.last_record_date || 0),
      showSorterTooltip: false,
      render: (timestamp: number | undefined) =>
        timestamp
          ? showRelativeTime
            ? formatRelative(timestamp)
            : format(new Date(timestamp * 1000), 'MMM dd, yyyy')
          : '-',
      align: 'right',
    },
  ]

  // Add actions column only for admins
  if (isAdmin) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record: Project) => {
        const isArchived = record.archived_at != null
        return (
          <DropdownMenu
            items={[
              {
                key: 'edit',
                label: 'Edit',
                onClick: (event) => {
                  event.stopPropagation()
                  onEditProject(record)
                },
              },
              {
                key: 'archive',
                disabled: isArchived,
                label: (
                  <ArchiveActionLabel>
                    Archive
                    {isArchived && (
                      <Tooltip
                        content={
                          <TooltipText>Project is already archived</TooltipText>
                        }
                      >
                        <ArchivedHelpIcon />
                      </Tooltip>
                    )}
                  </ArchiveActionLabel>
                ),
                onClick: (event) => {
                  event.stopPropagation()
                  onArchiveProject(record.uuid)
                },
              },
              {
                key: 'delete',
                label: 'Delete',
                danger: true,
                onClick: (event) => {
                  event.stopPropagation()
                  handleRemoveProject(record.uuid)
                },
              },
            ]}
          >
            <ActionButton
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              aria-label="More actions"
            >
              <EllipsisOutlined />
            </ActionButton>
          </DropdownMenu>
        )
      },
    })
  }

  const handleTableChange: NonNullable<TableProps<Project>['onChange']> = (
    ...args
  ) => {
    const extra = args[3]
    onTableChange(extra.currentDataSource as Project[])
  }

  const rowSelection: TableProps<Project>['rowSelection'] = isAdmin
    ? {
        fixed: true,
        selectedRowKeys,
        onChange: (keys) => onSelectedChange(keys as string[]),
        preserveSelectedRowKeys: true,
        getCheckboxProps: () =>
          ({
            'aria-label': 'Select project',
          }) as unknown as RowCheckboxProps,
      }
    : undefined

  return (
    <ProjectsWrapper>
      <StyledTable
        dataSource={projects || []}
        columns={columns}
        loading={loading}
        rowKey="uuid"
        size="small"
        sticky={{ offsetHeader: 0 }}
        scroll={{ x: 2100, y: 1 }}
        rowSelection={rowSelection}
        pagination={{
          current: currentPage,
          pageSize,
          onChange: onPageChange,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} hubs`,
        }}
        onChange={handleTableChange}
        rowClassName={(_, index) => {
          const absoluteIndex = (currentPage - 1) * pageSize + index
          return absoluteIndex === highlightedIndex
            ? 'is-clickable-row is-keyboard-focused'
            : 'is-clickable-row'
        }}
        onRow={(record) => ({
          role: 'link',
          tabIndex: 0,
          'aria-label': `Open project ${record.title}`,
          onClick: (event) => {
            if (event.defaultPrevented) {
              return
            }
            if (event.metaKey || event.ctrlKey) {
              window.open(
                projectPath(record.uuid),
                '_blank',
                'noopener,noreferrer',
              )
              return
            }
            navigate(projectPath(record.uuid))
          },
          onKeyDown: (event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              navigate(projectPath(record.uuid))
            }
            if (event.key === ' ') {
              event.preventDefault()
              navigate(projectPath(record.uuid))
            }
          },
          onMouseDown: (event) => {
            if (event.button === 1) {
              window.open(
                projectPath(record.uuid),
                '_blank',
                'noopener,noreferrer',
              )
            }
          },
        })}
      />
    </ProjectsWrapper>
  )
}
